import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import { DirectionContext } from "../App";
import { patchTermsAgreement } from "../config/termsAgreement";

const NewsProofSubmission = () => {
  const { navigateTo } = useContext(DirectionContext);

  // 아코디언 상태 관리 (유의사항 4개 섹션 모두 기본 펼침)
  const [expandedItems, setExpandedItems] = useState({
    registration: true,
    settlement: true,
    disclaimer: true,
    evidence: true,
  });

  // 전체 동의 체크박스 상태 관리
  const [allAgreed, setAllAgreed] = useState(false);

  // 아코디언 토글
  const toggleItem = (itemKey) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  };

  // 전체 동의 체크박스 토글
  const toggleAllAgreed = () => {
    setAllAgreed((prev) => !prev);
  };

  // 페이지 로드 시 스크롤 위치를 맨 위로 초기화
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);

  // 다음 페이지로 이동
  const handleNext = async () => {
    if (!allAgreed) {
      return;
    }

    // 유의사항 동의(가입시점요건 / 성공보수방식 / 증거자료 관련) 서버 반영
    try {
      const applicationId = Cookies.get("application_id");
      if (applicationId) {
        await patchTermsAgreement(applicationId, {
          hasAgreedToNotice: true,
          hasAgreedToSuccessFee: true,
        });
      }
    } catch (termsError) {
      // 동의 저장 실패는 흐름을 막지 않는다 (로그만 남김)
      console.error("유의사항 동의 저장 실패:", termsError);
    }

    // 신청서는 이미 ScreenshotSubmission.js에서 생성되었으므로 바로 다음 페이지로 이동
    navigateTo("/agreement");
  };

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 유의사항 내용 렌더링 함수
  const renderNoticeContent = (key) => {
    switch (key) {
      case "registration":
        return (
          <NoticeText>
            <BoldText>2025년 1월 28일까지 듀오에 가입하신 분</BoldText>, 또는
            듀오로부터 개인정보 유출 관련 개별 통지를 받으신 분이 신청
            대상입니다.
          </NoticeText>
        );
      case "settlement":
        return (
          <NoticeText>
            종결 심급에 따른 <BoldText>성공보수율(부가가치세 별도)</BoldText>:
            <BulletLine>제1심 10% / 항소심 12% / 상고심 15%</BulletLine>
            <BulletLine>
              합의·조정·화해 등 판결 외 종결: 진행 중인 심급의 비율 적용
            </BulletLine>
            법무법인이 판결금을 대리 수령하여, 7일 이내에 보수 공제 후 잔액을
            신청 계좌로 입금해드립니다.
          </NoticeText>
        );
      case "disclaimer":
        return (
          <NoticeText>
            본 집단소송은 사실관계와 법률적 판단에 따라 결과가 달라질 수 있으며,
            소송 결과를 확정적으로 보장드리기 어려움을 안내드립니다.
            {"\n\n"}
            변호인단은 적법한 절차에 따라 통상적인 주의의무와 전문적 판단으로
            사건을 수행하며, 그 범위 내에서 발생하는 소송 결과에 대하여는 책임을
            부담하지 않습니다.
          </NoticeText>
        );
      case "evidence":
        return (
          <NoticeText>
            <BoldText>
              개인정보보호위원회의 결과 통지 후 듀오가 발송할 개별 통지문
            </BoldText>
            이 본 소송의 핵심 증빙입니다. 수령 시 별도 안내에 따라 제출해주세요.
            소송 진행 중 듀오 측 대응이나 법원 요구에 따라 추가 자료 제출이
            필요할 수 있습니다.
          </NoticeText>
        );
      default:
        return null;
    }
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <Header>
            <SubTitle>유의사항</SubTitle>
            <Description>
              집단소송 참여를 위해
              <br />
              필요한 동의사항을 확인해주세요
            </Description>
          </Header>

          <NoticeList>
            {/* 가입 시점 요건 */}
            <NoticeItem>
              <NoticeHeader
                onClick={() => toggleItem("registration")}
                isExpanded={expandedItems.registration}
              >
                <NoticeTitle>가입 시점 요건</NoticeTitle>
                <ChevronIcon
                  src="/png/arrow.png"
                  alt="화살표"
                  isExpanded={expandedItems.registration}
                />
              </NoticeHeader>
              <NoticeContentWrapper
                isExpanded={expandedItems.registration}
                initial={false}
                animate={{
                  maxHeight: expandedItems.registration ? "500px" : 0,
                  opacity: expandedItems.registration ? 1 : 0,
                }}
                transition={{
                  maxHeight: {
                    duration: 0.5,
                    ease: [0.4, 0, 0.2, 1],
                  },
                  opacity: {
                    duration: 0.4,
                    ease: "easeInOut",
                    delay: expandedItems.registration ? 0.2 : 0,
                  },
                }}
              >
                <NoticeContentInner>
                  {renderNoticeContent("registration")}
                </NoticeContentInner>
              </NoticeContentWrapper>
            </NoticeItem>

            {/* 승소 시 성공보수 및 정산 방식 */}
            <NoticeItem>
              <NoticeHeader
                onClick={() => toggleItem("settlement")}
                isExpanded={expandedItems.settlement}
              >
                <NoticeTitle>승소 시 성공보수 및 정산 방식</NoticeTitle>
                <ChevronIcon
                  src="/png/arrow.png"
                  alt="화살표"
                  isExpanded={expandedItems.settlement}
                />
              </NoticeHeader>
              <NoticeContentWrapper
                isExpanded={expandedItems.settlement}
                initial={false}
                animate={{
                  maxHeight: expandedItems.settlement ? "500px" : 0,
                  opacity: expandedItems.settlement ? 1 : 0,
                }}
                transition={{
                  maxHeight: {
                    duration: 0.5,
                    ease: [0.4, 0, 0.2, 1],
                  },
                  opacity: {
                    duration: 0.4,
                    ease: "easeInOut",
                    delay: expandedItems.settlement ? 0.2 : 0,
                  },
                }}
              >
                <NoticeContentInner>
                  {renderNoticeContent("settlement")}
                </NoticeContentInner>
              </NoticeContentWrapper>
            </NoticeItem>

            {/* 면책고지 */}
            {/* <NoticeItem>
              <NoticeHeader
                onClick={() => toggleItem("disclaimer")}
                isExpanded={expandedItems.disclaimer}
              >
                <NoticeTitle>면책고지</NoticeTitle>
                <ChevronIcon
                  src="/png/arrow.png"
                  alt="화살표"
                  isExpanded={expandedItems.disclaimer}
                />
              </NoticeHeader>
              <NoticeContentWrapper
                isExpanded={expandedItems.disclaimer}
                initial={false}
                animate={{
                  maxHeight: expandedItems.disclaimer ? "500px" : 0,
                  opacity: expandedItems.disclaimer ? 1 : 0,
                }}
                transition={{
                  maxHeight: {
                    duration: 0.5,
                    ease: [0.4, 0, 0.2, 1],
                  },
                  opacity: {
                    duration: 0.4,
                    ease: "easeInOut",
                    delay: expandedItems.disclaimer ? 0.2 : 0,
                  },
                }}
              >
                <NoticeContentInner>
                  {renderNoticeContent("disclaimer")}
                </NoticeContentInner>
              </NoticeContentWrapper>
            </NoticeItem> */}

            {/* 증거자료 관련 안내 */}
            <NoticeItem>
              <NoticeHeader
                onClick={() => toggleItem("evidence")}
                isExpanded={expandedItems.evidence}
              >
                <NoticeTitle>증거자료 관련 안내</NoticeTitle>
                <ChevronIcon
                  src="/png/arrow.png"
                  alt="화살표"
                  isExpanded={expandedItems.evidence}
                />
              </NoticeHeader>
              <NoticeContentWrapper
                isExpanded={expandedItems.evidence}
                initial={false}
                animate={{
                  maxHeight: expandedItems.evidence ? "500px" : 0,
                  opacity: expandedItems.evidence ? 1 : 0,
                }}
                transition={{
                  maxHeight: {
                    duration: 0.5,
                    ease: [0.4, 0, 0.2, 1],
                  },
                  opacity: {
                    duration: 0.4,
                    ease: "easeInOut",
                    delay: expandedItems.evidence ? 0.2 : 0,
                  },
                }}
              >
                <NoticeContentInner>
                  {renderNoticeContent("evidence")}
                </NoticeContentInner>
              </NoticeContentWrapper>
            </NoticeItem>
          </NoticeList>

          <AllAgreeContainer>
            <CheckboxContainer onClick={toggleAllAgreed}>
              <Checkbox checked={allAgreed}>
                {allAgreed && <CheckIcon src="/png/checked.png" alt="체크" />}
              </Checkbox>
              <CheckboxLabel>
                위 내용을 모두 확인하고 동의합니다.{" "}
                <RequiredText>[필수]</RequiredText>
              </CheckboxLabel>
            </CheckboxContainer>
          </AllAgreeContainer>

          <ButtonsContainer>
            <NextButton
              onClick={handleNext}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={!allAgreed}
            >
              다음으로
            </NextButton>
          </ButtonsContainer>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #f7f7f7;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    background-color: #f7f7f7;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f7f7f7;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: 100vh;
    box-shadow: none;
    padding: 0 0 100px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
    margin-top: 40px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 20px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 10px;
  line-height: 1.4;
`;

const NoticeList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const NoticeItem = styled.div`
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const NoticeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  cursor: pointer;
`;

const NoticeTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const ChevronIcon = styled.img`
  width: 16px;
  height: 16px;
  transition: transform 0.3s ease;
  transform: ${(props) =>
    props.isExpanded ? "rotate(0deg)" : "rotate(180deg)"};
`;

const NoticeContentWrapper = styled(motion.div)`
  overflow: hidden;
  transform-origin: top;
  will-change: max-height, opacity;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
`;

const NoticeContentInner = styled.div`
  padding: 0 20px 20px 20px;
`;

const NoticeText = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 16px 0;
  white-space: pre-line;
`;

const BoldText = styled.span`
  font-weight: 700;
  color: #333;
`;

// 글머리 기호(•) + hanging indent: 줄바꿈된 wrapped 텍스트가 첫 글자 위치에 정렬됨
// 글머리는 absolute로 배치하여 본문 padding-left와 정확히 동일한 시작점에서 wrapping
const BulletLine = styled.div`
  position: relative;
  padding-left: 1.1em;
  margin: 6px 0;

  &::before {
    content: "•";
    position: absolute;
    left: 0.15em;
    top: 0;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
`;

const Checkbox = styled.div`
  width: 22px;
  height: 22px;
  border: 2px solid ${(props) => (props.checked ? "#E31C20" : "#ddd")};
  border-radius: 4px;
  background-color: ${(props) => (props.checked ? "#E31C20" : "white")};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
  transition: all 0.2s ease;
`;

const CheckIcon = styled.img`
  width: 14px;
  height: 14px;
`;

const CheckboxLabel = styled.span`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`;

const AllAgreeContainer = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto 20px auto;
  padding: 0;
`;

const RequiredText = styled.span`
  color: #e31c20;
  font-weight: 600;
`;

const ButtonsContainer = styled.div`
  max-width: 400px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-top: 0px;
  padding: 0 0px;
  background-color: #f8f8f8;
  margin: 0 auto;

  @media (max-width: 768px) {
    margin-top: 0px;
    padding-bottom: 40px;
  }

  @media (min-width: 769px) {
    padding: 0;
    background-color: transparent;
    justify-content: center;
  }
`;

const NextButton = styled(motion.button)`
  flex: 1;
  display: flex;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 20px;
  background: ${(props) => (props.disabled ? "#E0898A" : "#E31C20")};
  color: ${(props) => (props.disabled ? "#DDC2C2" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border: none;
  opacity: 1;
  pointer-events: auto;
`;

export default NewsProofSubmission;
